/**
 * Unified Print Utilities
 * Consolidates printReceiptBrowser and generatePrintHTML into a single module
 */

/**
 * Get paper dimensions based on paper size
 * @param {string} paperSize - "58mm" or "80mm"
 * @returns {Object} Paper dimension settings
 */
export const getPaperDimensions = (paperSize = "80mm") => {
  const is58mm = paperSize === "58mm";
  return {
    is58mm,
    pageWidth: is58mm ? "48mm" : "72mm",
    bodyWidth: is58mm ? "44mm" : "68mm",
    fontSize: is58mm ? "10px" : "12px",
    smallFont: is58mm ? "8px" : "10px",
    headerFont: is58mm ? "14px" : "18px",
    itemFont: is58mm ? "12px" : "14px",
    tableFont: is58mm ? "16px" : "20px",
  };
};

/**
 * Generate base CSS styles for thermal printing
 * @param {Object} dims - Paper dimensions from getPaperDimensions
 * @param {string} fontFamily - Font family to use
 * @returns {string} CSS styles
 */
export const getBasePrintStyles = (dims, fontFamily = "Courier New") => `
  @page { 
    margin: 0; 
    padding: 0;
    size: ${dims.pageWidth} auto; 
  }
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  body { 
    font-family: '${fontFamily}', 'Courier New', monospace; 
    font-size: ${dims.fontSize}; 
    width: ${dims.bodyWidth}; 
    margin: 0 auto; 
    padding: 2mm;
    line-height: 1.3;
  }
  .center { text-align: center; }
  .right { text-align: right; }
  .bold { font-weight: bold; }
  .divider { border-top: 1px dashed #000; margin: 2mm 0; }
  .item-row { 
    display: flex; 
    justify-content: space-between; 
    margin: 1mm 0;
    width: 100%;
  }
  .item-row span:last-child {
    text-align: right;
    min-width: 50px;
  }
  .total-row { 
    display: flex; 
    justify-content: space-between; 
    font-weight: bold; 
    margin: 1mm 0; 
    font-size: ${dims.is58mm ? "12px" : "14px"}; 
  }
  .item {
    display: flex;
    justify-content: space-between;
    font-size: ${dims.smallFont};
    margin: 1mm 0;
  }
  .item span:last-child {
    text-align: right;
    min-width: 50px;
  }
  .tax-line { color: #444; }
  @media print { 
    html, body { 
      width: ${dims.bodyWidth}; 
      margin: 0;
      padding: 2mm;
    }
  }
`;

/**
 * Render template header/footer items
 * @param {Array} items - Header or footer items from template
 * @param {string} fontSize - Font size to use
 * @returns {string} HTML string
 */
export const renderTemplateItems = (items, fontSize = "10px") => {
  if (!items || items.length === 0) return "";
  return items
    .map((item) => {
      const size = item.fontSize || fontSize;
      const weight = item.bold ? "font-weight:bold;" : "";
      const style = item.italic ? "font-style:italic;" : "";
      const text = item.type === "text" ? item.text : `${item.key}: ${item.value}`;
      return `<div class="center" style="font-size:${size};${weight}${style}">${text}</div>`;
    })
    .join("");
};

/**
 * Render tax breakdown
 * @param {Object} options - Tax rendering options
 * @returns {string} HTML string
 */
export const renderTaxBreakdown = ({
  showDetailed,
  taxBreakdown,
  taxTotal,
  tax,
  labels = {},
  smallFont = "10px",
}) => {
  if (showDetailed && taxBreakdown?.length > 0) {
    return taxBreakdown
      .map(
        (t) =>
          `<div class="item-row">
            <span>${t.name} (${t.rate}%)</span>
            <span>${(t.amount || 0).toFixed(2)}</span>
          </div>`
      )
      .join("");
  }
  const taxValue = taxTotal || tax || 0;
  return taxValue
    ? `<div class="item-row"><span>${labels.tax || "Tax"}</span><span>${taxValue.toFixed(2)}</span></div>`
    : "";
};

/**
 * Generate eTIMS fiscal section HTML
 * @param {Object} etims - eTIMS data with QR code
 * @param {string} kraPin - KRA PIN
 * @returns {string} HTML string
 */
export const generateEtimsSection = (etims, kraPin) => {
  if (!etims || !etims.submitted) return "";

  const isCreditNote = etims.document_type === "credit_note";
  const fiscalTitle = isCreditNote ? "eTIMS FISCAL CREDIT NOTE" : "eTIMS FISCAL RECEIPT";
  const invoiceLabel = isCreditNote ? "eTIMS Credit Note #" : "eTIMS Invoice #";

  return `
    <div class="divider" style="border-top:1px dashed #000; margin:8px 0;"></div>
    <div class="center" style="font-weight:bold;">${fiscalTitle}</div>
    <div class="center" style="font-size:10px;">CU Serial: ${etims.sdc_id || ""}</div>
    <div class="center" style="font-size:10px;">${invoiceLabel}: ${etims.invc_no || ""}</div>
    <div class="center" style="font-size:10px;">Receipt Sign: ${etims.rcpt_sign || ""}</div>
    ${kraPin ? `<div class="center" style="font-size:10px;">TIN: ${kraPin}</div>` : ""}
    ${
      etims.qr_base64
        ? `
      <div class="center" style="margin:8px 0;">
        <img src="data:image/png;base64,${etims.qr_base64}" style="width:120px;height:120px;" alt="eTIMS QR"/>
      </div>
      <div class="center" style="font-size:8px;">Scan to verify receipt on KRA eTIMS portal</div>
    `
        : ""
    }
  `;
};

/**
 * Generate Kitchen/Bar ticket HTML
 * @param {Object} data - Print data
 * @param {Object} dims - Paper dimensions
 * @param {Object} config - Destination config
 * @returns {string} Complete HTML document
 */
export const generateKitchenTicketHTML = (data, dims, config = {}) => {
  const fontFamily = config.font_family || "Courier New";

  // Helper to format item modifiers and prep instructions
  const formatItemDetails = (item) => {
    const details = [];
    
    // Prep options selected (e.g., "Well Done", "Medium")
    if (item.prep_options_selected?.length > 0) {
      details.push(`<div class="item-prep">▶ ${item.prep_options_selected.join(', ')}</div>`);
    }
    
    // Free text prep instructions
    if (item.prep_instructions) {
      details.push(`<div class="item-note">★ ${item.prep_instructions}</div>`);
    }
    
    // Selected options/modifiers with prices
    if (item.selected_options?.length > 0) {
      item.selected_options.forEach(opt => {
        const priceStr = opt.price > 0 ? ` (+${opt.price})` : '';
        details.push(`<div class="item-modifier">+ ${opt.option_name}${priceStr}</div>`);
      });
    }
    
    // Legacy modifiers array (backwards compatibility)
    if (item.modifiers?.length > 0 && !item.selected_options?.length) {
      item.modifiers.forEach(m => {
        details.push(`<div class="item-modifier">+ ${m}</div>`);
      });
    }
    
    // General notes
    if (item.notes) {
      details.push(`<div class="item-note">→ ${item.notes}</div>`);
    }
    
    // Meal slot selections (for combo meals)
    if (item.meal_selections?.length > 0) {
      item.meal_selections.forEach(slot => {
        details.push(`<div class="item-slot">${slot.slot_name}: ${slot.product_name}</div>`);
        if (slot.prep_instructions) {
          details.push(`<div class="item-note" style="margin-left:6mm;">★ ${slot.prep_instructions}</div>`);
        }
      });
    }
    
    return details.join('');
  };

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${(data.destination || "KITCHEN").toUpperCase()} Order</title>
        <style>
          @page { 
            margin: 0; 
            padding: 0;
            size: ${dims.pageWidth} auto; 
          }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: '${fontFamily}', 'Courier New', monospace; 
            font-size: ${dims.fontSize}; 
            padding: 2mm;
            width: ${dims.bodyWidth};
            margin: 0 auto;
            line-height: 1.3;
          }
          .header { 
            text-align: center; 
            font-size: ${dims.headerFont}; 
            font-weight: bold; 
            margin-bottom: 2mm; 
          }
          .divider { border-top: 1px dashed #000; margin: 2mm 0; }
          .table-num { 
            font-size: ${dims.tableFont}; 
            font-weight: bold; 
            text-align: center;
          }
          .order-info {
            font-size: ${dims.smallFont};
            text-align: center;
          }
          .item { 
            font-size: ${dims.itemFont}; 
            font-weight: bold; 
            margin: 2mm 0 0 0; 
          }
          .item-prep {
            font-size: ${dims.itemFont};
            font-weight: bold;
            margin-left: 3mm;
            color: #000;
          }
          .item-modifier {
            font-size: ${dims.smallFont};
            margin-left: 3mm;
            color: #333;
          }
          .item-slot {
            font-size: ${dims.smallFont};
            margin-left: 3mm;
            font-weight: bold;
          }
          .item-note {
            font-size: ${dims.smallFont};
            font-style: italic;
            margin-left: 3mm;
            color: #333;
          }
          .reprint {
            text-align: center;
            font-weight: bold;
            margin-top: 2mm;
          }
          @media print { 
            html, body { width: ${dims.bodyWidth}; margin: 0; padding: 2mm; }
          }
        </style>
      </head>
      <body>
        <div class="header">${(data.destination || "KITCHEN").toUpperCase()}</div>
        ${data.outlet_name ? `<div class="order-info" style="font-weight:bold;">${data.outlet_name}</div>` : ""}
        <div class="divider"></div>
        <div class="table-num">Table: ${data.table_number || "N/A"}</div>
        <div class="order-info">Order: ${data.order_number || ""}</div>
        <div class="order-info">${new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
        ${data.order_type ? `<div class="order-info">${data.order_type}</div>` : ""}
        ${data.waiter_name ? `<div class="order-info">Waiter: ${data.waiter_name}</div>` : ""}
        <div class="divider"></div>
        ${(data.items || [])
          .map(
            (item) => `
          <div class="item">${item.quantity}x ${item.name}</div>
          ${formatItemDetails(item)}
        `
          )
          .join("")}
        <div class="divider"></div>
        ${data.is_reprint ? '<div class="reprint">*** REPRINT ***</div>' : ""}
        <div style="height:3mm;"></div>
      </body>
    </html>
  `;
};

/**
 * Generate Receipt HTML for browser printing
 * Unified function that replaces both printReceiptBrowser and generatePrintHTML for receipts
 * @param {Object} data - Print data from backend
 * @param {Object} template - Invoice template settings
 * @returns {string} Complete HTML document
 */
export const generateReceiptHTML = (data, template = {}) => {
  const t = template;
  const labels = t.labels || {};
  const paperSize = t.paper_size || "80mm";
  const dims = getPaperDimensions(paperSize);
  const fontFamily = t.font_family || "Courier New";

  // Format number with 2 decimal places
  const formatCurrency = (num) => {
    return (num || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Helper to format item add-ons WITH prices only (for receipts/invoices)
  const formatItemAddOnsForReceipt = (item) => {
    const details = [];
    
    // Only show selected options/modifiers that have a price
    if (item.selected_options?.length > 0) {
      item.selected_options.forEach(opt => {
        if (opt.price && opt.price > 0) {
          details.push(`<div style="font-size:${dims.smallFont};padding-left:3mm;">+ ${opt.option_name} (+${formatCurrency(opt.price)})</div>`);
        }
      });
    }
    
    // Meal slot selections with price adjustments
    if (item.meal_selections?.length > 0) {
      item.meal_selections.forEach(slot => {
        if (slot.price_adjustment && slot.price_adjustment > 0) {
          details.push(`<div style="font-size:${dims.smallFont};padding-left:3mm;">${slot.slot_name}: ${slot.product_name} (+${formatCurrency(slot.price_adjustment)})</div>`);
        }
      });
    }
    
    return details.join('');
  };

  // Render items - optionally grouped by category
  const renderItems = () => {
    const items = data.items || [];
    if (t.group_products_by_category && items.length > 0) {
      const grouped = {};
      items.forEach((item) => {
        const cat = item.category_name || "Other";
        if (!grouped[cat]) grouped[cat] = [];
        grouped[cat].push(item);
      });

      return Object.entries(grouped)
        .map(
          ([cat, catItems]) => `
          <div style="font-weight:bold;margin-top:5px;text-align:center">-- ${cat} --</div>
          ${catItems
            .map(
              (item) => `
              <div class="item-row">
                <span>${item.quantity}x ${item.name}</span>
                <span>${formatCurrency((item.unit_price || item.price || 0) * item.quantity)}</span>
              </div>
              ${formatItemAddOnsForReceipt(item)}
            `
            )
            .join("")}
        `
        )
        .join("");
    }

    return items
      .map(
        (item) => `
        <div class="item-row">
          <span>${item.quantity}x ${item.name}</span>
          <span>${formatCurrency((item.unit_price || item.price || 0) * item.quantity)}</span>
        </div>
        ${formatItemAddOnsForReceipt(item)}
      `
      )
      .join("");
  };

  // Header items
  const headerItemsHtml = renderTemplateItems(t.header_items, dims.smallFont);

  // Footer items
  const footerItemsHtml = t.footer_items?.length
    ? renderTemplateItems(t.footer_items, dims.smallFont)
    : `<div class="center" style="font-size:${dims.smallFont};">Thank you for your business!</div>`;

  // Tax breakdown
  const taxHtml = renderTaxBreakdown({
    showDetailed: t.show_detailed_taxes,
    taxBreakdown: data.tax_breakdown,
    taxTotal: data.tax_total,
    tax: data.tax,
    labels,
    smallFont: dims.smallFont,
  });

  // eTIMS section
  const etimsHtml = generateEtimsSection(data.etims, data.kra_pin);

  // Card / M-Pesa payment details block (#4, opt-in via template)
  const cp = data.card_payment || {};
  const showPaymentDetails = (t.show_payment_details ?? data.show_payment_details) && Object.keys(cp).length > 0;
  let paymentDetailsHtml = "";
  if (showPaymentDetails) {
    const rows = [];
    if (Array.isArray(cp.receipt_lines) && cp.receipt_lines.length) {
      cp.receipt_lines.forEach((ln) => {
        if (ln == null) return;
        if (typeof ln === "string") rows.push(`<div class="item-row"><span>${ln}</span><span></span></div>`);
        else if (ln.textLeft != null || ln.textRight != null)
          rows.push(`<div class="item-row"><span>${ln.textLeft || ""}</span><span>${ln.textRight || ""}</span></div>`);
        else if (ln.text != null) rows.push(`<div class="item-row"><span>${ln.text}</span><span></span></div>`);
      });
    } else {
      if (cp.card_type) rows.push(`<div class="item-row"><span>Card</span><span>${cp.card_type}${cp.card_last4 ? ` ****${cp.card_last4}` : ""}</span></div>`);
      if (cp.auth_code) rows.push(`<div class="item-row"><span>Approval</span><span>${cp.auth_code}</span></div>`);
      if (cp.rrn) rows.push(`<div class="item-row"><span>Receipt</span><span>${cp.rrn}</span></div>`);
      if (cp.mpesa_receipt) rows.push(`<div class="item-row"><span>M-Pesa Ref</span><span>${cp.mpesa_receipt}</span></div>`);
    }
    if (rows.length) {
      const proc = String(cp.processor || "");
      const head = proc.startsWith("dpo") || proc === "network"
        ? "Network / DPO Pay"
        : proc === "pesapal"
        ? "Pesapal Payment"
        : "Payment Details";
      paymentDetailsHtml = `<div class="divider"></div><div style="font-weight:bold;text-transform:uppercase;font-size:${dims.smallFont};">${head}</div>${rows.join("")}`;
    }
  }

  // Reprint indicator with count
  const reprintHtml = data.print_count > 1 
    ? `<div class="center" style="font-weight:bold;margin-top:2mm;">*** REPRINT ${data.print_count - 1} ***</div>` 
    : "";

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${data.document_title || "Receipt"} - ${data.invoice_number || data.order_number}</title>
      <style>${getBasePrintStyles(dims, fontFamily)}</style>
    </head>
    <body>
      ${(t.logo_url || t.logo_base64) ? `<div class="center" style="margin-bottom:2mm;"><img src="${t.logo_base64 || t.logo_url}" style="width:${({small:35,medium:55,large:80}[t.logo_size] || 55)}%;max-height:${dims.is58mm ? '18mm' : '24mm'};object-fit:contain;" alt="Logo"/></div>` : ""}
      <div class="center bold" style="font-size:${dims.is58mm ? "12px" : "14px"};">${data.business_name || data.property_name || ""}</div>
      <div class="center" style="font-size:${dims.smallFont};">${data.business_address || data.property_address || ""}</div>
      ${data.business_phone || data.property_phone ? `<div class="center" style="font-size:${dims.smallFont};">Tel: ${data.business_phone || data.property_phone}</div>` : ""}
      ${data.kra_pin ? `<div class="center" style="font-size:${dims.smallFont};">PIN: ${data.kra_pin}</div>` : ""}
      ${headerItemsHtml}
      <div class="divider"></div>
      ${data.document_title ? `<div class="center bold" style="font-size:${dims.is58mm ? "13px" : "15px"};margin-bottom:2mm;">${data.document_title}</div>` : ""}
      <div style="font-size:${dims.smallFont};">${labels.invoice_number || "Inv#"}: ${data.invoice_number || data.order_number || ""}</div>
      ${data.original_invoice_number ? `<div style="font-size:${dims.smallFont};">Original Invoice: ${data.original_invoice_number}</div>` : ""}
      ${data.credit_note_reason ? `<div style="font-size:${dims.smallFont};">Reason: ${data.credit_note_reason}</div>` : ""}
      <div style="font-size:${dims.smallFont};">${labels.date || "Date"}: ${data.date ? new Date(data.date).toLocaleDateString() : new Date().toLocaleDateString()} ${data.date ? new Date(data.date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
      ${t.show_table_number !== false && data.table_number ? `<div style="font-size:${dims.smallFont};">${labels.table || "Table"}: ${data.table_number}</div>` : ""}
      ${t.show_order_type !== false && data.order_type ? `<div style="font-size:${dims.smallFont};">Type: ${data.order_type}</div>` : ""}
      ${t.show_customer_details !== false && data.customer_name ? `<div style="font-size:${dims.smallFont};">${labels.customer || "Customer"}: ${data.customer_name}</div>` : ""}
      ${t.show_server_name !== false && (data.served_by || data.server_name) ? `<div style="font-size:${dims.smallFont};">${labels.server || "Server"}: ${data.served_by || data.server_name}</div>` : ""}
      <div class="divider"></div>
      ${renderItems()}
      <div class="divider"></div>
      ${data.subtotal ? `<div class="item-row"><span>${labels.subtotal || "Subtotal"}</span><span>${formatCurrency(data.subtotal)}</span></div>` : ""}
      ${taxHtml}
      ${data.service_charge ? `<div class="item-row"><span>Service Charge</span><span>${formatCurrency(data.service_charge)}</span></div>` : ""}
      ${data.discount ? `<div class="item-row"><span>${labels.discount || "Discount"}</span><span>-${formatCurrency(data.discount)}</span></div>` : ""}
      <div class="total-row"><span>${labels.total || "TOTAL"}</span><span>KES ${formatCurrency(data.total)}</span></div>
      ${data.payment_method ? `<div class="item-row"><span>Paid (${(data.payment_method || "").toUpperCase()})</span><span>KES ${formatCurrency(data.amount_paid || data.total)}</span></div>` : ""}
      ${(data.change_amount && data.change_amount > 0) ? `<div class="item-row"><span>Change</span><span>KES ${formatCurrency(data.change_amount)}</span></div>` : ""}
      ${(data.balance_due && data.balance_due > 0) ? `<div class="item-row" style="font-weight:bold;"><span>Balance Due</span><span>KES ${formatCurrency(data.balance_due)}</span></div>` : ""}
      ${data.mpesa_receipt ? `<div class="center" style="font-size:${dims.smallFont};">M-Pesa Ref: ${data.mpesa_receipt}</div>` : ""}
      ${t.show_loyalty_points !== false && data.points_earned ? `<div class="center" style="font-size:${dims.smallFont};">Points: +${data.points_earned}</div>` : ""}
      ${paymentDetailsHtml}
      ${etimsHtml}
      <div class="divider"></div>
      ${footerItemsHtml}
      ${t.show_qr_code && t.qr_code_url ? `<div class="center" style="margin-top:2mm;"><div style="font-size:8px;">Scan for feedback</div></div>` : ""}
      ${reprintHtml}
      <div style="height:3mm;"></div>
    </body>
    </html>
  `;
};

/**
 * Build a DPO / N-Genius card-terminal receipt slip (customer copy) as thermal HTML.
 * @param {Array} lines - custReceipt lines {text, textLeft, textRight, style, src}
 * @param {string} paperSize - "58mm" or "80mm"
 * @param {string} businessName
 * @returns {string} full HTML document
 */
export const generateCardSlipHTML = (lines = [], paperSize = "80mm", businessName = "") => {
  const is58 = paperSize === "58mm";
  const mm = is58 ? "58mm" : "80mm";
  const base = is58 ? 11 : 13;
  const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  let body = `<div class="c name">${esc(businessName || "HostPoint POS")}</div>`;
  body += `<div class="c title">Card Payment Receipt</div><div class="hr"></div>`;
  (lines || []).forEach((ln) => {
    const bold = /bold|title|large|double/i.test(ln.style || "");
    if (ln.src && /^data:image/i.test(ln.src)) {
      body += `<div class="c"><img src="${ln.src}" style="max-width:100%"/></div>`;
    } else if (ln.textLeft != null || ln.textRight != null) {
      body += `<div class="kv${bold ? " b" : ""}"><span class="l">${esc(ln.textLeft || "")}</span><span class="r">${esc(ln.textRight || "")}</span></div>`;
    } else if (ln.text != null) {
      body += `<div class="c${bold ? " b" : ""}">${esc(ln.text)}</div>`;
    } else {
      body += `<div>&nbsp;</div>`;
    }
  });
  body += `<div class="hr"></div><div class="c foot">Printed ${new Date().toLocaleString("en-KE")}</div>`;
  return `<!DOCTYPE html><html><head><meta charset="utf-8"/>
  <style>
    @page { size: ${mm} auto; margin: 0; }
    * { font-family: 'Courier New', monospace; color: #000; box-sizing: border-box; }
    html, body { width: ${mm}; }
    body { padding: 6px 8px; margin: 0; font-size: ${base}px; }
    .c { text-align: center; }
    .b { font-weight: bold; }
    .name { font-weight: bold; font-size: ${base + 3}px; }
    .title { font-weight: bold; font-size: ${base + 1}px; margin: 2px 0 4px; text-transform: uppercase; }
    .kv { display: flex; justify-content: space-between; gap: 8px; line-height: 1.55; }
    .kv .r { text-align: right; white-space: nowrap; }
    .kv .l { word-break: break-word; }
    .hr { border-top: 1px dashed #000; margin: 5px 0; }
    .foot { font-size: ${base - 1}px; margin-top: 3px; }
  </style></head><body>${body}</body></html>`;
};


/**
 * Open browser print dialog with the provided HTML
 * @param {string} html - HTML content to print
 * @param {string} title - Window title
 */
export const openPrintWindow = (html, title = "Print") => {
  const printWindow = window.open("", "_blank", "width=300,height=600");
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  }
};

/**
 * Generate print HTML based on data type (receipt or kitchen ticket)
 * This is the main entry point that replaces both generatePrintHTML and printReceiptBrowser
 * @param {Object} data - Print data from backend
 * @param {string} destination - "receipt", "kitchen", or "bar"
 * @param {Object} template - Invoice template settings
 * @param {Object} destConfig - Destination configuration
 * @returns {string} Complete HTML document
 */
export const generatePrintHTML = (data, destination = "receipt", template = {}, destConfig = {}) => {
  const paperSize = destConfig?.paper_size || template?.paper_size || "80mm";
  const dims = getPaperDimensions(paperSize);

  // Kitchen/Bar tickets have different format
  if (data.type === "kitchen_ticket" || destination === "kitchen" || destination === "bar") {
    return generateKitchenTicketHTML(data, dims, destConfig);
  }

  // Receipt/Invoice
  return generateReceiptHTML(data, template);
};

/**
 * Generate Shift Summary HTML for 80mm thermal printing
 */
export const generateShiftSummaryHTML = (data, paperSize = "80mm") => {
  const dims = getPaperDimensions(paperSize);
  const cur = data.currency || "KES";
  const fmt = (v) => `${cur} ${Number(v || 0).toLocaleString("en-KE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtDate = (d) => {
    if (!d) return "-";
    try { return new Date(d).toLocaleString("en-KE", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return d; }
  };

  const paymentRows = Object.entries(data.payment_totals || {})
    .filter(([, v]) => v > 0)
    .map(([m, v]) => `<div class="item-row"><span>${m.charAt(0).toUpperCase() + m.slice(1)}</span><span>${fmt(v)}</span></div>`)
    .join("");

  const cashSection = data.status === "closed" ? `
    <div class="divider"></div>
    <div class="center bold" style="margin-bottom:2mm">CASH RECONCILIATION</div>
    <div class="item-row"><span>Opening Cash</span><span>${fmt(data.opening_cash)}</span></div>
    <div class="item-row"><span>Cash Sales</span><span>${fmt(data.payment_totals?.cash || 0)}</span></div>
    <div class="item-row bold"><span>Expected Cash</span><span>${fmt(data.expected_cash)}</span></div>
    <div class="item-row"><span>Closing Cash</span><span>${fmt(data.closing_cash)}</span></div>
    <div class="item-row bold" style="font-size:${dims.is58mm ? "12px" : "14px"}">
      <span>Variance</span>
      <span style="color:${data.cash_difference === 0 ? "#000" : data.cash_difference > 0 ? "#000" : "#000"}">
        ${data.cash_difference >= 0 ? "+" : ""}${fmt(data.cash_difference)}
      </span>
    </div>
    ${data.bank_deposit ? `<div class="item-row"><span>Bank Deposit</span><span>${fmt(data.bank_deposit)}</span></div>` : ""}
    ${data.float_retained ? `<div class="item-row"><span>Float Retained</span><span>${fmt(data.float_retained)}</span></div>` : ""}
  ` : "";

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
<style>${getBasePrintStyles(dims)}
  .section-title { font-weight: bold; text-align: center; margin: 2mm 0 1mm; font-size: ${dims.is58mm ? "11px" : "13px"}; }
</style></head><body>
  <div class="center bold" style="font-size:${dims.headerFont}">${data.business_name || "Shift Summary"}</div>
  ${data.business_address ? `<div class="center" style="font-size:${dims.smallFont}">${data.business_address}</div>` : ""}
  ${data.business_phone ? `<div class="center" style="font-size:${dims.smallFont}">Tel: ${data.business_phone}</div>` : ""}
  ${data.outlet_name ? `<div class="center" style="font-size:${dims.smallFont}">${data.outlet_name}</div>` : ""}

  <div class="divider"></div>
  <div class="center bold" style="font-size:${dims.is58mm ? "12px" : "14px"}">SHIFT SUMMARY REPORT</div>
  <div class="divider"></div>

  <div class="item-row"><span>Shift #</span><span>${data.shift_number || 1}</span></div>
  <div class="item-row"><span>Date</span><span>${data.business_date || ""}</span></div>
  <div class="item-row"><span>Status</span><span>${(data.status || "").toUpperCase()}</span></div>
  <div class="item-row"><span>Opened By</span><span>${data.opened_by || ""}</span></div>
  <div class="item-row"><span>Start</span><span>${fmtDate(data.start_time)}</span></div>
  ${data.end_time ? `<div class="item-row"><span>End</span><span>${fmtDate(data.end_time)}</span></div>` : ""}
  ${data.closed_by ? `<div class="item-row"><span>Closed By</span><span>${data.closed_by}</span></div>` : ""}

  <div class="divider"></div>
  <div class="center bold" style="margin-bottom:2mm">SALES SUMMARY</div>
  <div class="item-row bold" style="font-size:${dims.is58mm ? "13px" : "16px"}"><span>TOTAL SALES</span><span>${fmt(data.total_sales)}</span></div>
  <div class="item-row"><span>Total Orders</span><span>${data.total_orders || 0}</span></div>
  <div class="item-row"><span>Tax Collected</span><span>${fmt(data.total_tax)}</span></div>
  ${data.total_discount > 0 ? `<div class="item-row"><span>Discounts</span><span>-${fmt(data.total_discount)}</span></div>` : ""}

  ${paymentRows ? `
    <div class="divider"></div>
    <div class="center bold" style="margin-bottom:2mm">PAYMENTS BY METHOD</div>
    ${paymentRows}
  ` : ""}

  ${cashSection}

  ${data.void_count > 0 || data.cancelled_orders > 0 ? `
    <div class="divider"></div>
    <div class="center bold" style="margin-bottom:2mm">VOIDS & CANCELLATIONS</div>
    ${data.void_count > 0 ? `<div class="item-row"><span>Voided Items</span><span>${data.void_count}</span></div>
    <div class="item-row"><span>Void Value</span><span>${fmt(data.void_value)}</span></div>` : ""}
    ${data.cancelled_orders > 0 ? `<div class="item-row"><span>Cancelled Orders</span><span>${data.cancelled_orders}</span></div>` : ""}
  ` : ""}

  ${data.notes ? `
    <div class="divider"></div>
    <div style="font-size:${dims.smallFont}"><strong>Notes:</strong> ${data.notes}</div>
  ` : ""}

  <div class="divider"></div>
  <div class="center" style="font-size:${dims.smallFont};margin-top:2mm">
    Printed: ${new Date().toLocaleString("en-KE")}
  </div>
  <div class="center" style="font-size:${dims.smallFont};margin-top:4mm">--- End of Shift Report ---</div>
</body></html>`;
};

export default {
  getPaperDimensions,
  getBasePrintStyles,
  renderTemplateItems,
  renderTaxBreakdown,
  generateEtimsSection,
  generateKitchenTicketHTML,
  generateReceiptHTML,
  generatePrintHTML,
  generateShiftSummaryHTML,
  openPrintWindow,
};
